import type Phaser from 'phaser';
import { hudRects } from '../ui/hud-obstacles.js';
import { type Box, clearOf } from './hud-clearance.js';

/** Over every sprite and name plate: the arrow is the one thing to follow. */
const POINTER_DEPTH = 1_000_000;
const FILL = 0xffd23b;
const OUTLINE = 0x2a1a06;
/** A downward arrow, one character per pixel; the other three are this one turned. */
const ARROW_DOWN = [
  '..#####..',
  '..#####..',
  '..#####..',
  '#########',
  '.#######.',
  '..#####..',
  '...###...',
  '....#....',
];
/** Distance kept from the screen's edge when the goal is out of sight. */
const EDGE_MARGIN = 10;
const BOB_MS = 700;
const BOB_PX = 3;
/** Kept between the arrow and the HUD, wider than the bob so it never dips under. */
const HUD_GAP = 4 + BOB_PX;

type Heading = 'down' | 'up' | 'left' | 'right';

function turned(rows: readonly string[], heading: Heading): string[] {
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  const at = (x: number, y: number): string => rows[y]?.[x] ?? '.';
  if (heading === 'down') return [...rows];
  if (heading === 'up') return [...rows].reverse();
  // Transposed, so the tip that pointed down now points right (or left).
  const columns = Array.from({ length: width }, (_, x) =>
    Array.from({ length: height }, (_, y) => at(x, y)).join(''),
  );
  return heading === 'right' ? columns : columns.map((row) => row.split('').reverse().join(''));
}

/**
 * The tutorial's arrow: it bobs over whatever the current step asks for (a
 * rat, the pelts on the ground, the merchant, the arch), and waits at the
 * screen's edge, pointing, while that is out of sight. Drawn pixel by pixel
 * so it stays on the grid at any zoom (CLAUDE.md rule 6). A hint drawn by
 * this client only: it reads the synchronised state, it decides nothing.
 */
export class TutorialPointer {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private heading?: Heading;

  constructor(private readonly scene: Phaser.Scene) {
    this.graphics = scene.add.graphics().setDepth(POINTER_DEPTH).setVisible(false);
  }

  /**
   * Point at `target` (world pixels, the arrow's tip), or hide with nothing
   * to point at. `topInset` (world pixels) is the strip the objective banner
   * covers: the arrow never waits under it.
   */
  point(target: { x: number; y: number } | undefined, time: number, topInset = 0): void {
    if (target === undefined) {
      this.graphics.setVisible(false);
      return;
    }
    const full = this.scene.cameras.main.worldView;
    const top = full.top + topInset;
    const view = {
      left: full.left,
      right: full.right,
      top,
      bottom: full.bottom,
      width: full.width,
      height: full.bottom - top,
      centerX: full.centerX,
      centerY: (top + full.bottom) / 2,
    };
    const bob = Math.round(((Math.sin((time / BOB_MS) * Math.PI * 2) + 1) / 2) * BOB_PX);
    const size = { w: ARROW_DOWN[0]?.length ?? 0, h: ARROW_DOWN.length };
    const inside =
      target.x >= view.left + EDGE_MARGIN &&
      target.x <= view.right - EDGE_MARGIN &&
      target.y - size.h >= view.top + EDGE_MARGIN &&
      target.y <= view.bottom - EDGE_MARGIN;
    let heading: Heading = 'down';
    let x = target.x - Math.floor(size.w / 2);
    let y = target.y - size.h;
    if (!inside) {
      const dx = target.x - view.centerX;
      const dy = target.y - view.centerY;
      heading =
        Math.abs(dx) * view.height > Math.abs(dy) * view.width
          ? dx > 0
            ? 'right'
            : 'left'
          : dy > 0
            ? 'down'
            : 'up';
      const w = heading === 'left' || heading === 'right' ? size.h : size.w;
      const h = heading === 'left' || heading === 'right' ? size.w : size.h;
      const clampX = Math.min(
        Math.max(target.x - w / 2, view.left + EDGE_MARGIN),
        view.right - EDGE_MARGIN - w,
      );
      const clampY = Math.min(
        Math.max(target.y - h / 2, view.top + EDGE_MARGIN),
        view.bottom - EDGE_MARGIN - h,
      );
      x =
        heading === 'left'
          ? view.left + EDGE_MARGIN
          : heading === 'right'
            ? view.right - EDGE_MARGIN - w
            : clampX;
      y =
        heading === 'up'
          ? view.top + EDGE_MARGIN
          : heading === 'down'
            ? view.bottom - EDGE_MARGIN - h
            : clampY;
    }
    const turnedSize =
      heading === 'left' || heading === 'right' ? { w: size.h, h: size.w } : size;
    // Out from under the HUD first, then the bob, which the gap leaves room for.
    const clear = clearOf(
      { x, y, w: turnedSize.w, h: turnedSize.h },
      this.hudInWorld(time),
      { x: view.left, y: view.top, w: view.width, h: view.height },
    );
    // Over the goal it bobs up off it; at the edge, out towards it.
    const push = inside ? -1 : 1;
    x = clear.x + (heading === 'left' ? -bob : heading === 'right' ? bob : 0);
    y = clear.y + (heading === 'up' ? -bob : heading === 'down' ? bob * push : 0);
    if (heading !== this.heading) this.draw(heading);
    this.graphics.setPosition(Math.round(x), Math.round(y)).setVisible(true);
  }

  /** The HUD's rectangles (`ui/hud-obstacles.ts`) in world pixels, grown by the gap. */
  private hudInWorld(time: number): Box[] {
    const camera = this.scene.cameras.main;
    const view = camera.worldView;
    const canvas = this.scene.game.canvas.getBoundingClientRect();
    if (canvas.width === 0 || canvas.height === 0) return [];
    const sx = view.width / canvas.width;
    const sy = view.height / canvas.height;
    return hudRects(time).map((rect) => ({
      x: view.x + (rect.left - canvas.left) * sx - HUD_GAP,
      y: view.y + (rect.top - canvas.top) * sy - HUD_GAP,
      w: rect.width * sx + HUD_GAP * 2,
      h: rect.height * sy + HUD_GAP * 2,
    }));
  }

  destroy(): void {
    this.graphics.destroy();
  }

  private draw(heading: Heading): void {
    this.heading = heading;
    const rows = turned(ARROW_DOWN, heading);
    this.graphics.clear();
    // The outline first, one pixel around every filled pixel, then the fill.
    this.graphics.fillStyle(OUTLINE, 1);
    rows.forEach((row, y) => {
      row.split('').forEach((cell, x) => {
        if (cell === '#') this.graphics.fillRect(x - 1, y - 1, 3, 3);
      });
    });
    this.graphics.fillStyle(FILL, 1);
    rows.forEach((row, y) => {
      row.split('').forEach((cell, x) => {
        if (cell === '#') this.graphics.fillRect(x, y, 1, 1);
      });
    });
  }
}
